import {IAlert} from '../../../../actions/proto';
import {businessRulesActionMap} from '../../../../constants';
import {priority} from '@priority-software/priority-proto-api/lib';
import {AutomationChoose, AutomationChooseOptions} from '../../../../reducer/model';
import {texts} from '../../../../texts';
import {AlertListItem} from '../../../../reducer/types';
import {multiOptionsHaveValidValue} from '../../../../utils';
import {RecipientsType} from '../../../RuleDialog/parts/AutomationActionTemplate/BusinessRuleActionTemplate/enum';
import {AlertValidForCategory, RuleType} from '../../../../enums';
import {systemLang} from '../../../../../../Localization/languages';
import {getLang, isRTL} from '../../../../../../Localization';
import {getUsername} from '../../../../../../_shared/GWTBridge/invoke/genPage';

export const mapValidFor = (
    formData: priority.netitems.form.IAlertForm | null,
    chooses: AutomationChooseOptions
) => {
    if (!formData) return '';

    if (formData.ValidMySelf) return texts.myself_label;

    if (formData.ValidAllUsers) return texts.all_users_label;

    if (formData.ValidUserInGroup) {
        return (
            chooses.PrivilegeGroups?.filter(item =>
                formData.ValidGroup.includes(item.value as number)
            )
                ?.map(item => item.label)
                .join(', ') || texts.my_privilege_group_label
        );
    }
    if (multiOptionsHaveValidValue(formData.ValidGroup as number[])) {
        return (
            chooses.PrivilegeGroups?.filter(item =>
                formData.ValidGroup?.includes(item.value as number)
            )
                ?.map(item => item.label)
                .join(', ') || ''
        );
    }

    if (multiOptionsHaveValidValue(formData.ValidUser as number[])) {
        return (
            chooses.User?.filter(item => formData.ValidUser?.includes(item.value as number))
                .map(item => item.label)
                .join(', ') || ''
        );
    }
    return '';
};

export const prepareListData = (
    data: IAlert[],
    chooses: AutomationChooseOptions,
    type: RuleType
): AlertListItem[] => {
    const isAutofill = type === RuleType.autofill;
    const isBPMAutomation = type === RuleType.bpm;

    return data?.map(item => {
        const autofillData = isAutofill
            ? {
                  autofillData: {
                      sourceField: item.Form
                          ? chooses.Fields.find(field => field.value === item.Form.UpdateField)
                                ?.label || ''
                          : '',
                      action:
                          chooses.Ops.find(item => item.value === 382)?.label ||
                          texts.value_changes_label,
                      targetField:
                          chooses.Fields.find(field => field.value === Number(item.RecipientField))
                              ?.label || '',
                      autofillValue: item.RecipientMail?.[0] || ''
                  }
              }
            : {};

        return {
            description: item.Description,
            alertNumber: Number(item.AlertNumber),
            conditions: item.AlertConds.map(cond => ({
                currentValue:
                    chooses.CurrrentValues?.find(item => item.value === cond.CurrentValue)?.label ||
                    '',
                conditionOperator:
                    chooses.Ops?.find(item => item.value === cond.ConditionOperator)?.label || '',
                conditionField:
                    chooses.Fields?.find(col => col.value === cond.ConditionField)?.label || '',
                conditionValue: cond.ConditionValue,
                conditionSecondValue: cond.ConditionSecondValue
            })),
            conditionOperator: item.ConditionsOperator
                ? texts.operator_or_label
                : texts.operator_and_label,
            validFor: mapValidFor(item.Form, chooses),
            updatedBy: item.LastMobifyBy,
            ruleAction: item.MessageType ? Number(item.MessageType) : undefined,
            ruleActionText:
                (item.MessageType &&
                    chooses.MessageTypes.find(option => option.value === item.MessageType)
                        ?.label) ||
                '',
            isActive: item.Active,
            isLocked: isBPMAutomation && item.LastMobifyBy !== getUsername(),
            ...autofillData
        };
    });
};

export const prepareChooses = (
    data: priority.netitems.form.IAlertChoosesRs,
    systemLangs: systemLang[]
) => {
    return Object.keys(data).reduce((prev, key) => {
        const chooseData = data[key];
        if (Array.isArray(chooseData)) {
            if (key === 'Documents') {
                prev[key] = chooseData.map((item: priority.netitems.form.IAlertChooseDocuments) => {
                    const langsDictionary = systemLangs.reduce((prev, curr) => {
                        return {...prev, [curr.lang]: curr.langtitle};
                    }, {});
                    return {
                        label: item.Title,
                        value: item.Id, // Should be removed when BE will be fixed
                        data: {
                            printFormats: item.PrintFormates.map(format => {
                                return {
                                    label: format.Title,
                                    value: format.Id
                                };
                            }),
                            printTemplates: item.PrintTemplates.map(format => {
                                const formatLanguageNumber = format.Language || getLang();
                                const formatLanguage =
                                    langsDictionary[formatLanguageNumber as number];

                                return {
                                    label: `${format.Title} (${formatLanguage})`,
                                    value: format.Id
                                };
                            })
                        }
                    };
                });
            } else if (key === 'ValidTypes') {
                prev[key] = chooseData.map((item: priority.netitems.form.IAlertChoose) => {
                    const data = {
                        value: item.Id,
                        label: item.Title,
                        data: item
                    };
                    if (item.Id === AlertValidForCategory.userInGroup) {
                        data.label = texts.my_privilege_group_label;
                    } else if (item.Id === AlertValidForCategory.group) {
                        data.label = texts.spec_privilege_group_label;
                    } else if (item.Id === AlertValidForCategory.user) {
                        data.label = texts.specific_user_option_text;
                    }
                    return data;
                });
            } else if (key === 'MailTypes') {
                prev[key] = chooseData.map((item: priority.netitems.form.IAlertChoose) => {
                    const data = {
                        value: item.Id,
                        label: item.Title,
                        data: item
                    };
                    if (data.value === RecipientsType.email) {
                        data.label = texts.email_field_label;
                    }
                    return data;
                });
            } else if (key === 'MessageTypes') {
                prev[key] = chooseData.map((item: priority.netitems.form.IAlertChoose) => {
                    const customData = businessRulesActionMap[Number(item.Id)];

                    return {
                        value: item.Id,
                        label: texts[customData.textField] || item.Title,
                        icon: customData?.icon,
                        description: item.Description
                    } as AutomationChoose;
                });
            } else if (key === 'EnglishFields') {
                prev[key] = chooseData.map((engField: priority.netitems.form.IAlertChoose) => {
                    const heFields = isRTL()
                        ? data['Fields']?.find(field => field.Id === engField.Id) || {}
                        : {};

                    return {
                        value: engField.Id,
                        label: isRTL() ? heFields.Title : engField.Title,
                        description: engField.Title,
                        data: engField
                    } as AutomationChoose;
                });
            } else {
                prev[key] = chooseData.map((item: priority.netitems.form.IAlertChoose) => {
                    return {
                        value: item.Id,
                        label: item.Title,
                        description: item.Description,
                        data: item
                    } as AutomationChoose;
                });
            }
        }
        return prev;
    }, {}) as AutomationChooseOptions;
};
